import React from 'react';
import { LessonStagePanel } from '../../shared/LessonStagePanel';
import { useLessonContext } from '../../LessonContext';

export function LessonGeneration() {
  const { document } = useLessonContext();
  const hasSetup = !!document?.course_context;

  return (
    <LessonStagePanel
      stageId="lesson_generation"
      title="Lesson Generation"
      description="Generate the complete lesson content with extended thinking. Produces all sections, text, exercise specs, and media descriptions in a single call."
      isLlmStage
      runDisabled={!hasSetup}
      runDisabledReason={!hasSetup ? 'Lesson setup data not loaded.' : undefined}
      renderOutput={renderLessonOutput}
    />
  );
}

function renderLessonOutput(document: Record<string, unknown>): React.ReactNode {
  const lesson: any = document.lesson_content;
  if (!lesson) {
    return <div className="text-sm text-gray-500">No lesson generated yet.</div>;
  }

  const sections = lesson.sections || [];

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span>Sections: {sections.length}</span>
        <span>Read time: ~{lesson.estimated_read_time_minutes}m</span>
        <span>Exercises: {lesson.exercise_count}</span>
      </div>

      {lesson.teaching_arc_notes && (
        <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-md text-xs text-indigo-800 italic">
          {lesson.teaching_arc_notes}
        </div>
      )}

      {/* Sections */}
      {sections.map((section: any, sIdx: number) => (
        <SectionDisplay key={section.section_id || sIdx} section={section} />
      ))}
    </div>
  );
}

function SectionDisplay({ section }: { section: any }) {
  const content = section.content || [];

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <div>
          <span className="text-xs font-mono text-gray-400 mr-2">{section.section_id}</span>
          <span className="text-sm font-medium text-gray-900">{section.title || '(Opening)'}</span>
        </div>
        {section.purpose && (
          <span className="text-xs text-gray-400 italic max-w-md truncate">{section.purpose}</span>
        )}
      </div>
      <div className="p-4 space-y-3">
        {content.map((item: any, i: number) => (
          <ContentItemDisplay key={i} item={item} />
        ))}
      </div>
    </div>
  );
}

function ContentItemDisplay({ item }: { item: any }) {
  const typeColors: Record<string, { bg: string; border: string; label: string }> = {
    text: { bg: 'bg-white', border: 'border-gray-100', label: 'text-gray-400' },
    exercise: { bg: 'bg-green-50', border: 'border-green-200', label: 'text-green-600' },
    image: { bg: 'bg-purple-50', border: 'border-purple-200', label: 'text-purple-600' },
    comparison: { bg: 'bg-amber-50', border: 'border-amber-200', label: 'text-amber-600' },
    callout: { bg: 'bg-cyan-50', border: 'border-cyan-200', label: 'text-cyan-600' },
    audio: { bg: 'bg-pink-50', border: 'border-pink-200', label: 'text-pink-600' },
  };
  const colors = typeColors[item.type] || { bg: 'bg-gray-50', border: 'border-gray-200', label: 'text-gray-500' };

  if (item.type === 'text') {
    return (
      <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
        {item.body}
      </div>
    );
  }

  if (item.type === 'exercise') {
    return (
      <div className={`${colors.bg} border ${colors.border} rounded-md p-3`}>
        <div className={`text-[10px] font-bold uppercase ${colors.label} mb-1`}>Exercise</div>
        <div className="text-xs text-gray-700">{item.intent}</div>
        {item.context_from_text && (
          <div className="text-xs text-gray-500 mt-1 italic">Context: {item.context_from_text}</div>
        )}
      </div>
    );
  }

  if (item.type === 'comparison') {
    return (
      <div className={`${colors.bg} border ${colors.border} rounded-md p-3`}>
        <div className={`text-[10px] font-bold uppercase ${colors.label} mb-2`}>Comparison</div>
        {item.purpose && <div className="text-xs text-gray-500 mb-2 italic">{item.purpose}</div>}
        <div className="grid grid-cols-2 gap-3">
          {(item.items || []).map((comp: any, i: number) => (
            <div key={i} className="bg-white border border-gray-200 rounded p-2">
              <div className="text-xs font-medium text-gray-700 mb-1">{comp.label}</div>
              <div className="text-xs text-gray-600">{comp.content}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (item.type === 'callout') {
    return (
      <div className={`${colors.bg} border ${colors.border} rounded-md p-3`}>
        <div className="text-sm text-gray-800 whitespace-pre-wrap">{item.body}</div>
      </div>
    );
  }

  if (item.type === 'image') {
    return (
      <div className={`${colors.bg} border ${colors.border} rounded-md p-3`}>
        <div className={`text-[10px] font-bold uppercase ${colors.label} mb-1`}>Image</div>
        <div className="text-xs text-gray-700">{item.description}</div>
        {item.alt_text && <div className="text-xs text-gray-500 mt-1">Alt: {item.alt_text}</div>}
      </div>
    );
  }

  if (item.type === 'audio') {
    return (
      <div className={`${colors.bg} border ${colors.border} rounded-md p-3`}>
        <div className={`text-[10px] font-bold uppercase ${colors.label} mb-1`}>Audio</div>
        <div className="text-xs text-gray-700">{item.description}</div>
        {item.transcript_hint && <div className="text-xs text-gray-500 mt-1">Transcript: {item.transcript_hint}</div>}
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
      <div className="text-[10px] font-bold uppercase text-gray-400 mb-1">{item.type}</div>
      <pre className="text-xs text-gray-600">{JSON.stringify(item, null, 2)}</pre>
    </div>
  );
}
